// Global Types
import type {
  $Children,
  $Component,
} from 'common.types';
import type {
  $FullRegattaEntry,
  $FullRegattaEntryCheck,
  $Model,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  GroupType,
} from 'common.data';

// Global Components
import * as React from 'react';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import {
  animationClass,
  getImageUrl,
} from '@vroskus/library-helpers/dist/browser';
import {
  getDateTimeFormats,
  getRegattaEntryCrewmemberNames,
  getRegattaEntrySkipperName,
} from 'common.helpers/dist/browser';
import {
  getRegattaGroupName,
} from '../../../helpers';
import config from '../../../config';

// Components
import RegistrationNo from '../../common/RegistrationNo';

// Types
import type {
  $ReducerState,
} from '../../../redux';

export type $OwnProps = $Translate & {
  children: $Children;
  item: $FullRegattaEntry;
  regatta: $Model<'Regatta'>;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  visible: boolean;
};

class RegattaEntryDialogView extends React.Component<$Props, $State> {
  wrapperRef: React.RefObject<HTMLDivElement>;

  handleClickOutsideBind: (event) => void;

  constructor(props: $Props) {
    super(props);

    this.wrapperRef = React.createRef();

    this.handleClickOutsideBind = this.handleClickOutside.bind(this);

    this.state = {
      visible: false,
    };
  }

  componentDidMount(): void {
    document.addEventListener(
      'mousedown',
      this.handleClickOutsideBind,
    );
  }

  componentWillUnmount(): void {
    document.removeEventListener(
      'mousedown',
      this.handleClickOutsideBind,
    );
  }

  handleClickOutside(event): void {
    if (this.wrapperRef
      && this.wrapperRef.current !== null
      && !this.wrapperRef.current.contains(event.target)
    ) {
      this.setState({
        visible: false,
      });
    }
  }

  childrenWithToggleHandler(): $Component<unknown> | null {
    const {
      children,
    } = this.props;

    if (children && React.isValidElement(children)) {
      return React.cloneElement(
        children,
        {
          // @ts-expect-error dialog
          onClick: () => this.setState({
            visible: true,
          }),
        },
      );
    }

    return null;
  }

  hide(): void {
    this.setState({
      visible: false,
    });
  }

  renderRegattaEntryChecks() {
    const {
      item,
      t,
    } = this.props;

    const data: Array<$FullRegattaEntryCheck> = item.RegattaEntryChecks;

    return (data.length > 0 ? (
      <div className={'mt-5 text-left'}>
        <small className={'text-gray-600 uppercase'}>
          {t('Checks')}
        </small>
        <div className={'grid xl:grid-cols-3 xl:gap-3'}>
          {data.map((dataItem: $FullRegattaEntryCheck) => dataItem.RegattaCheck && (
            <div
              className={'flex items-center'}
              key={dataItem.id}
            >
              <em className={`la ${dataItem.Value ? 'la-check text-green-600' : 'la-times text-red-600'} mr-2`} />
              <span>
                {dataItem.RegattaCheck.Name}
              </span>
            </div>
          ))}
        </div>
      </div>
    ) : null);
  }

  renderRegattaEntryRatings() {
    const {
      item,
      main,
      t,
    } = this.props;

    const formats = getDateTimeFormats({
      RegionId: main.RegionId,
      regions: main.regions,
    });

    const dateTimeFormat = `${formats.Date} ${formats.Time}`;

    const data: Array<$Model<'Rating'>> = item.Ratings;

    return (data.length > 0 ? (
      <div className={'text-left mt-5'}>
        <small className={'text-gray-600 uppercase'}>
          {t('Ratings')}
        </small>
        <table className={'table w-full'}>
          <thead>
            <tr>
              <th className={'uppercase'}>
                {t('Type')}
              </th>
              <th className={'uppercase'}>
                {t('Date')}
              </th>
              <th className={'uppercase text-right'}>
                {t('Value')}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((rating: $Model<'Rating'>) => (
              <tr key={rating.id}>
                <td>
                  {rating.Type}
                </td>
                <td>
                  {moment(rating.createdAt).format(dateTimeFormat)}
                </td>
                <td className={'text-right'}>
                  <span className={'text-gray-600'}>
                    {`${rating.KeyValueName}: `}
                  </span>
                  <span>
                    {rating.KeyValue}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : null);
  }

  renderModalHeader(dataItem: $FullRegattaEntry) {
    const {
      regatta,
    } = this.props;

    return (
      <div className={'modal-header'}>
        <h3 className={'modal-title text-left h3'}>
          <RegistrationNo
            item={dataItem}
            regatta={regatta}
          />
          {dataItem.Name && (
            <span>
              {` ${dataItem.Name}`}
            </span>
          )}
        </h3>
        <button
          className={'close la la-times'}
          onClick={() => this.hide()}
          type={'button'}
        />
      </div>
    );
  }

  renderSportRegattaEntryData() {
    const {
      item,
      t,
    } = this.props;

    const groupCrewmemberQuantity: number = item.RegattaGroup.Group.CrewQuantity;

    const isCoachEnabled: boolean = item.RegattaGroup.CoachEnabled;

    const groupName: string = getRegattaGroupName(item.RegattaGroup) || t('Ungrouped');

    return (
      <div className={'grid xl:grid-cols-3 gap-3 text-left'}>
        <div>
          <small className={'text-gray-600 uppercase'}>
            {t('Group')}
          </small>
          <div>
            {groupName}
          </div>
        </div>
        {isCoachEnabled === true && (
          <div>
            <small className={'text-gray-600 uppercase'}>
              {t('Coach')}
            </small>
            <div>
              {item.CoachName}
            </div>
          </div>
        )}
        <div>
          <small className={'text-gray-600 uppercase'}>
            {t('Club')}
          </small>
          <div>
            {item.Club}
          </div>
        </div>
        <div>
          <small className={'text-gray-600 uppercase'}>
            {t('Skipper')}
          </small>
          <div>
            {getRegattaEntrySkipperName({
              regattaEntry: item,
            })}
          </div>
        </div>
        {groupCrewmemberQuantity !== 1 && (
          <div className={'col-span-2'}>
            <small className={'text-gray-600 uppercase'}>
              {t('Crew')}
            </small>
            <div>
              {getRegattaEntryCrewmemberNames({
                excludeSkipper: true,
                regattaEntry: item,
              }).join(', ')}
            </div>
          </div>
        )}
        {item.TrackerId !== null && (
          <div>
            <small className={'text-gray-600 uppercase'}>
              {t('Tracker ID')}
            </small>
            <div>
              {item.TrackerId}
            </div>
          </div>
        )}
      </div>
    );
  }

  renderCruiserRegattaEntryData() {
    const {
      item,
      t,
    } = this.props;

    return (
      <div className={'grid xl:grid-cols-4 gap-4 text-left'}>
        <div>
          <small className={'text-gray-600 uppercase'}>
            {t('Group')}
          </small>
          <div>
            {_.get(
              item,
              'RegattaGroup.AltName',
            ) || _.get(
              item,
              'RegattaGroup.Group.Name',
              t('Ungrouped'),
            )}
          </div>
        </div>
        <div>
          <small className={'text-gray-600 uppercase'}>
            {t('Make / Model')}
          </small>
          <div>
            {item.Model}
          </div>
        </div>
        <div>
          <small className={'text-gray-600 uppercase'}>
            {t('Club')}
          </small>
          <div>
            {item.Club}
          </div>
        </div>
        <div>
          <small className={'text-gray-600 uppercase'}>
            {t('Harbor')}
          </small>
          <div>
            {item.Harbor}
          </div>
        </div>
        <div>
          <small className={'text-gray-600 uppercase'}>
            {t('Skipper')}
          </small>
          <div>
            {getRegattaEntrySkipperName({
              regattaEntry: item,
            })}
          </div>
        </div>
        <div className={'col-span-3'}>
          <small className={'text-gray-600 uppercase'}>
            {t('Crew')}
          </small>
          <div>
            {getRegattaEntryCrewmemberNames({
              excludeSkipper: true,
              regattaEntry: item,
            }).join(', ')}
          </div>
        </div>
      </div>
    );
  }

  renderRegattaEntryData() {
    const {
      item,
    } = this.props;

    const isSportGroup: boolean = _.get(
      item,
      'RegattaGroup.Group.Type',
    ) === GroupType.sport;

    return isSportGroup === true
      ? this.renderSportRegattaEntryData()
      : this.renderCruiserRegattaEntryData();
  }

  render() {
    const {
      item,
      t,
    } = this.props;
    const {
      visible,
    } = this.state;

    return (
      <>
        {this.childrenWithToggleHandler()}
        <div
          className={`modal bg-gray-300 bg-opacity-50 ${visible === true ? 'active' : ''} ${animationClass('fadeIn')}`}
          ref={this.wrapperRef}
        >
          <div className={'modal-dialog max-w-2xl mx-auto'}>
            {item !== null && (
              <div className={'modal-content w-full'}>
                {this.renderModalHeader(item)}
                {item.Image !== null && (
                  <>
                    <div
                      className={'h-32 bg-center bg-fixed'}
                      style={{
                        backgroundImage: `url('${getImageUrl({
                          cdnUrl: config.CDN_URL,
                          image: item.Image,
                          size: 'max',
                        })}')`,
                      }}
                    />
                    <hr />
                  </>
                )}
                <div className={'modal-body'}>
                  {this.renderRegattaEntryData()}
                  {this.renderRegattaEntryChecks()}
                  {this.renderRegattaEntryRatings()}
                </div>
                <div className={'modal-footer'}>
                  <div className={'flex ml-auto'}>
                    <button
                      className={'btn btn_secondary btn_outlined uppercase'}
                      onClick={() => this.hide()}
                      type={'button'}
                    >
                      {t('Close')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default RegattaEntryDialogView;
